import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "esperron_content_cache_v1";

const ContentContext = createContext(null);

// Migrate legacy maquina -> maquinas on the fly (for cached content too)
const migrate = (data) => {
  if (!data) return data;
  if (!data.maquinas) {
    if (data.maquina && typeof data.maquina === "object") {
      data.maquinas = [{ id: "m1", ...data.maquina }];
    } else {
      data.maquinas = [];
    }
  }
  if (data.maquina) delete data.maquina;
  return data;
};

// Read last-known-good content from localStorage synchronously (no flash)
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return migrate(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const ContentProvider = ({ children, fallback }) => {
  const [content, setContent] = useState(() => readCache() || migrate(fallback));
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      if (data) {
        const migrated = migrate(data);
        setContent(migrated);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(migrated));
        } catch {}
      }
    } catch {
      // keep whatever we already have (cache or fallback)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, setContent, refresh, loading }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside <ContentProvider>");
  return ctx;
};
